import React, { useEffect } from 'react';
import './Wrapper.scss';
import useAccountData from '../../../hooks/account/UseAccountData';
import Loader from '../loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { ACCOUNTS, ADMIN, PROJECTS } from '../../../routes/path';
import { useAccount } from '../../../contexts/account/AccountContexts';
import useBilling from '../../../hooks/account/UseBilling';
import usePermission from '../../../hooks/global/UsePermission';
import { useAccountSubscriptionSocket } from '../../../hooks/socket/UseAccountSubscriptionSocket';
import {
  AccessMode,
  AdminResource,
  getAdminPermissions,
} from '../../../models/admin/permissions/AdminPermission';

interface WrapperProps {
  children: React.ReactNode;
  checkSuperAdmin?: boolean;
  canShowPricing?: boolean;
  /**
   * With `canShowPricing`: the user also needs read access to the BILLING resource, not just an account
   * with the billing addon — the same rule that decides the Settings "Subscription" tab. Off by default
   * because trial-ended and disabled-account screens share `canShowPricing` and must stay reachable.
   */
  requireBillingPermission?: boolean;
  requiredAdminResource?: AdminResource;
  requiredAdminAccess?: AccessMode;
}

const Wrapper: React.FC<WrapperProps> = ({
  children,
  checkSuperAdmin = false,
  canShowPricing = false,
  requireBillingPermission = false,
  requiredAdminResource,
  requiredAdminAccess = 'read',
}) => {
  const { projectId } = useParams();
  const loading = useAccountData(projectId);
  const navigate = useNavigate();
  const { accountResponse } = useAccount();
  const canShowBilling = useBilling();
  const billingAccess = usePermission('BILLING');
  useAccountSubscriptionSocket();

  useEffect(() => {
    if (
      !loading &&
      canShowPricing &&
      (!canShowBilling || (requireBillingPermission && !billingAccess.read))
    ) {
      navigate(PROJECTS);
    }

    if (!loading && checkSuperAdmin) {
      if (!accountResponse || !accountResponse.isSuperAdmin) {
        navigate(PROJECTS);
      } else if (requiredAdminResource) {
        const permissions = getAdminPermissions(accountResponse);
        if (!permissions[requiredAdminResource][requiredAdminAccess]) {
          // No access to this surface — fall back to Accounts when that's viewable, otherwise out of
          // admin entirely (avoids a redirect loop when Accounts itself is what's forbidden).
          const canViewAccounts =
            requiredAdminResource !== AdminResource.ACCOUNTS &&
            permissions[AdminResource.ACCOUNTS].read;
          navigate(canViewAccounts ? `${ADMIN}${ACCOUNTS}` : PROJECTS);
        }
      }
    }
  }, [
    checkSuperAdmin,
    canShowPricing,
    requireBillingPermission,
    canShowBilling,
    billingAccess.read,
    navigate,
    loading,
    requiredAdminResource,
    requiredAdminAccess,
  ]);

  if (loading) {
    return <Loader />;
  }

  return <div>{children}</div>;
};

export default Wrapper;
